/**
 * The plan editor's form model (TMMRSL-0049), pure: a plan's values as locale-formatted input
 * strings, the client checks that turn them back into a write body, the Simple / Advanced switch,
 * and the map from the server's field names (`fields` / `VALIDATION_FAILED`) to the form's fields.
 * Messages are catalog keys plus params; the component translates them. The agreement's limits are
 * the server's to enforce — the client only checks that each value is well-formed.
 *
 * An Advanced plan's phases start at month 1 with no gaps and no overlaps, so a phase's "from" is
 * never typed: row 0 starts at 1, and row i at row i−1's "to" + 1. Only the last row's "to" may be
 * blank ("onward").
 *
 * The extras (TMMRSL-0051, TMMAPI-0105) — a bounty, up to five milestone bonuses and two caps — are
 * ADVANCED only. A write omits a key it has no value for (TMMAPI-0167): SIMPLE clears amounts
 * (`0` / `[]`) and leaves out `bountyOnPayment`; a form that is not checking extras leaves out
 * those keys.
 */
import type {
  CommissionPlan,
  CommissionPlanAfterEnd,
  CommissionPlanAppliesTo,
  CommissionPlanMode,
  CommissionPlanWrite,
  PlanExtrasWrite,
  PlanMilestone,
} from '@/api/commissionPlans';
import type { PhaseInput } from '@/api/generated';
import { bpToPercentInput, parsePercentInput } from '@/features/rateCards/percentInput';
import { minorToMoneyInput, parseMoneyToMinor } from '@/i18n/format';

export interface PhaseForm {
  to: string;
  rate: string;
}

/** A milestone bonus row: "at" qualified referrals, and its amount. */
export interface MilestoneForm {
  at: string;
  amount: string;
}

export interface PlanForm {
  name: string;
  mode: CommissionPlanMode;
  appliesTo: CommissionPlanAppliesTo;
  simpleRate: string;
  simpleLifetime: boolean;
  simpleMonths: string;
  phases: PhaseForm[];
  coolOff: string;
  afterEnd: CommissionPlanAfterEnd;
  bountyOn: boolean;
  bounty: string;
  /** `'1'`…`'12'`. */
  bountyOnPayment: string;
  milestones: MilestoneForm[];
  referralCapOn: boolean;
  referralCap: string;
  monthCapOn: boolean;
  monthCap: string;
}

export type PhaseField = `phases.${number}.to` | `phases.${number}.rate`;
export type MilestoneField = `milestones.${number}.at` | `milestones.${number}.amount`;

export type FormField =
  | 'name'
  | 'mode'
  | 'appliesTo'
  | 'simpleRate'
  | 'simpleMonths'
  | PhaseField
  | 'coolOff'
  | 'afterEnd'
  | 'bounty'
  | 'bountyOnPayment'
  | MilestoneField
  | 'referralCap'
  | 'monthCap';

/** A client error: a catalog key and its params. */
export interface FormError {
  key: string;
  params?: Record<string, string | number>;
}

export type FormErrors = Partial<Record<FormField, FormError>>;

export const NAME_MAX = 80;
export const MAX_PHASES = 6;
export const MAX_MILESTONES = 5;
/** The bounty is earned on one of the referral's first twelve collected payments. */
export const BOUNTY_PAYMENTS = 12;

const PHASE_FIELDS: PhaseField[] = Array.from({ length: MAX_PHASES }, (_, i) => [
  `phases.${i}.to` as const,
  `phases.${i}.rate` as const,
]).flat();

const MILESTONE_FIELDS: MilestoneField[] = Array.from({ length: MAX_MILESTONES }, (_, i) => [
  `milestones.${i}.at` as const,
  `milestones.${i}.amount` as const,
]).flat();

/** The form's order — Tab order, and the first errored field to focus. */
export const FIELD_ORDER: FormField[] = [
  'name',
  'mode',
  'appliesTo',
  'simpleRate',
  'simpleMonths',
  ...PHASE_FIELDS,
  'coolOff',
  'afterEnd',
  'bounty',
  'bountyOnPayment',
  ...MILESTONE_FIELDS,
  'referralCap',
  'monthCap',
];

const WHOLE = /^\d+$/;
const PERCENT_MAX_BP = 10_000;

/** `minorToMoneyInput`, or the bare number when the currency is blank or unknown to Intl. */
function moneyInput(minor: number | null, currency: string, locale: string): string {
  if (minor === null) return '';
  try {
    return minorToMoneyInput(minor, currency, locale);
  } catch {
    return String(minor);
  }
}

/** `parseMoneyToMinor`, or `null` when the currency is blank or unknown to Intl. */
function parseMoney(value: string, currency: string, locale: string): number | null {
  try {
    return parseMoneyToMinor(value, currency, locale);
  } catch {
    return null;
  }
}

/** The form's first phase row, or a blank one: an Advanced form always has a row. */
function phaseForms(plan: CommissionPlan, locale: string): PhaseForm[] {
  const rows = plan.phases.map((phase) => ({
    to: phase.toMonth === null ? '' : String(phase.toMonth),
    rate: bpToPercentInput(phase.rateBp, locale),
  }));
  return rows.length > 0 ? rows : [{ to: '', rate: '' }];
}

/**
 * Both modes are seeded, so switching mode keeps what's there. The extras are all off or empty
 * when the plan has none (`extras` null); money is in the plan's currency.
 */
export function planToForm(plan: CommissionPlan, locale: string): PlanForm {
  const rows = phaseForms(plan, locale);
  const first = plan.phases[0];
  const extras = plan.extras;
  const money = (minor: number | null) => moneyInput(minor, plan.currency, locale);
  return {
    name: plan.name,
    mode: plan.mode,
    appliesTo: plan.appliesTo,
    simpleRate: first ? bpToPercentInput(first.rateBp, locale) : '',
    simpleLifetime: first ? first.toMonth === null : false,
    simpleMonths: first && first.toMonth !== null ? String(first.toMonth) : '',
    phases: plan.mode === 'SIMPLE' ? rows.slice(0, 1) : rows,
    coolOff: String(plan.coolOffDays),
    afterEnd: plan.afterEnd,
    bountyOn: extras?.bountyMinor != null,
    bounty: money(extras?.bountyMinor ?? null),
    // Editor only. A null payment is 1 and a null milestone list is no rows; the unedited write keeps null.
    bountyOnPayment: String(extras?.bountyOnPayment ?? 1),
    milestones: (extras?.milestones ?? []).map((milestone) => ({
      at: String(milestone.qualifiedReferrals),
      amount: money(milestone.amountMinor),
    })),
    referralCapOn: extras?.perReferralCapMinor != null,
    referralCap: money(extras?.perReferralCapMinor ?? null),
    monthCapOn: extras?.perAffiliateMonthCapMinor != null,
    monthCap: money(extras?.perAffiliateMonthCapMinor ?? null),
  };
}

/**
 * The plan's own values as a write body — the preview of a plan that isn't being edited. SIMPLE
 * leaves out the extras keys. ADVANCED sends the plan's extras: unset amounts are `0`; a null
 * payment number and a null milestone list are left out (`[]` would remove the list).
 */
export function planToBody(plan: CommissionPlan): CommissionPlanWrite {
  const terms = {
    name: plan.name,
    mode: plan.mode,
    appliesTo: plan.appliesTo,
    coolOffDays: plan.coolOffDays,
    afterEnd: plan.afterEnd,
    phases: plan.phases.map((phase) => phaseBody(phase.fromMonth, phase.toMonth, phase.rateBp)),
    version: plan.version,
  };
  if (plan.mode === 'SIMPLE') return terms;
  const extras = plan.extras;
  return {
    ...terms,
    bountyMinor: extras.bountyMinor ?? 0,
    ...(extras.bountyOnPayment !== null ? { bountyOnPayment: extras.bountyOnPayment } : {}),
    ...(extras.milestones !== null ? { milestones: extras.milestones } : {}),
    perReferralCapMinor: extras.perReferralCapMinor ?? 0,
    perAffiliateMonthCapMinor: extras.perAffiliateMonthCapMinor ?? 0,
  };
}

/** One phase of a write body; an open-ended phase omits `toMonth` (never `null`). */
function phaseBody(fromMonth: number, toMonth: number | null, rateBp: number): PhaseInput {
  return toMonth === null ? { fromMonth, rateBp } : { fromMonth, toMonth, rateBp };
}

/**
 * Each row's derived "from": 1 for row 0, then the previous row's "to" + 1. `null` once a "to"
 * above is blank or not a whole number.
 */
export function phaseFroms(phases: PhaseForm[]): Array<number | null> {
  const out: Array<number | null> = [];
  let next: number | null = 1;
  for (const phase of phases) {
    out.push(next);
    const to = phase.to.trim();
    next = next !== null && WHOLE.test(to) ? Number(to) + 1 : null;
  }
  return out;
}

type Parsed<T> = { ok: true; value: T } | { ok: false; error: FormError };

function parseRate(value: string, locale: string): Parsed<number> {
  const trimmed = value.trim();
  if (trimmed === '') return { ok: false, error: { key: 'commissionPlans.form.required' } };
  const bp = parsePercentInput(trimmed, locale);
  if (bp === null || bp < 0 || bp > PERCENT_MAX_BP) {
    return { ok: false, error: { key: 'commissionPlans.form.percent' } };
  }
  return { ok: true, value: bp };
}

function parseWhole(value: string, min: number): Parsed<number> {
  const trimmed = value.trim();
  if (trimmed === '') return { ok: false, error: { key: 'commissionPlans.form.required' } };
  if (!WHOLE.test(trimmed) || Number(trimmed) < min) {
    return { ok: false, error: { key: 'commissionPlans.form.wholeNumber' } };
  }
  return { ok: true, value: Number(trimmed) };
}

/** A money amount > 0 in `currency`. */
function parseAmount(value: string, currency: string, locale: string): Parsed<number> {
  const trimmed = value.trim();
  if (trimmed === '') return { ok: false, error: { key: 'commissionPlans.form.required' } };
  const minor = parseMoney(trimmed, currency, locale);
  if (minor === null || minor <= 0) {
    return { ok: false, error: { key: 'commissionPlans.form.amount' } };
  }
  return { ok: true, value: minor };
}

/** The extras in use: a bounty, any milestone row, or a cap. Simple has none. */
export function extrasInUse(form: PlanForm): boolean {
  return form.bountyOn || form.milestones.length > 0 || form.referralCapOn || form.monthCapOn;
}

/** The extras of a write body; SIMPLE clears amounts and milestones and leaves out payment. Errors go into `errors`. */
function extrasBody(
  form: PlanForm,
  locale: string,
  currency: string,
  errors: FormErrors,
): PlanExtrasWrite {
  if (form.mode === 'SIMPLE') {
    return {
      bountyMinor: 0,
      milestones: [],
      perReferralCapMinor: 0,
      perAffiliateMonthCapMinor: 0,
    };
  }

  let bountyMinor = 0;
  let bountyOnPayment = 1;
  if (form.bountyOn) {
    const bounty = parseAmount(form.bounty, currency, locale);
    if (bounty.ok) bountyMinor = bounty.value;
    else errors.bounty = bounty.error;
    const payment = form.bountyOnPayment.trim();
    if (WHOLE.test(payment) && Number(payment) >= 1 && Number(payment) <= BOUNTY_PAYMENTS) {
      bountyOnPayment = Number(payment);
    } else {
      errors.bountyOnPayment = { key: 'commissionPlans.form.paymentNumber' };
    }
  }

  const milestones: PlanMilestone[] = [];
  if (form.milestones.length > MAX_MILESTONES) {
    errors[`milestones.${MAX_MILESTONES - 1}.at`] = {
      key: 'commissionPlans.form.maxMilestones',
      params: { max: MAX_MILESTONES },
    };
  }
  let previous: number | null = null;
  for (const [i, row] of form.milestones.entries()) {
    const at = parseWhole(row.at, 1);
    if (!at.ok) errors[`milestones.${i}.at`] = at.error;
    else if (previous !== null && at.value <= previous) {
      errors[`milestones.${i}.at`] = { key: 'commissionPlans.form.ascending' };
    }
    const amount = parseAmount(row.amount, currency, locale);
    if (!amount.ok) errors[`milestones.${i}.amount`] = amount.error;
    if (at.ok) previous = at.value;
    if (at.ok && amount.ok) {
      milestones.push({ qualifiedReferrals: at.value, amountMinor: amount.value });
    }
  }

  function cap(on: boolean, value: string, field: 'referralCap' | 'monthCap'): number {
    if (!on) return 0;
    const parsed = parseAmount(value, currency, locale);
    if (parsed.ok) return parsed.value;
    errors[field] = parsed.error;
    return 0;
  }

  return {
    bountyMinor,
    bountyOnPayment,
    milestones,
    perReferralCapMinor: cap(form.referralCapOn, form.referralCap, 'referralCap'),
    perAffiliateMonthCapMinor: cap(form.monthCapOn, form.monthCap, 'monthCap'),
  };
}

/** Whether the body carries the extras (the plan was read with them), and the plan's currency. */
export interface ExtrasOptions {
  extras: boolean;
  currency: string;
}

/**
 * The form as a write body, or `null` with the client errors (catalog keys) when any value is
 * malformed. The body sends `phases` with `fromMonth` computed; SIMPLE sends its one phase. An
 * open-ended phase has no `toMonth` key at all. `version` is left out here; the screen stamps
 * the plan's version on save. With `extras`, the bounty, milestones and caps are checked and sent;
 * without, those keys are left out and are not checked.
 */
export function formToBody(
  form: PlanForm,
  locale: string,
  options: ExtrasOptions = { extras: false, currency: '' },
): { body: CommissionPlanWrite | null; errors: FormErrors } {
  const errors: FormErrors = {};

  const name = form.name.trim();
  if (name === '') errors.name = { key: 'commissionPlans.form.required' };
  else if (name.length > NAME_MAX) {
    errors.name = { key: 'commissionPlans.form.nameTooLong', params: { max: NAME_MAX } };
  }

  const phases: PhaseInput[] = [];
  if (form.mode === 'SIMPLE') {
    const rate = parseRate(form.simpleRate, locale);
    if (!rate.ok) errors.simpleRate = rate.error;
    let toMonth: number | null = null;
    if (!form.simpleLifetime) {
      const months = parseWhole(form.simpleMonths, 1);
      if (months.ok) toMonth = months.value;
      else errors.simpleMonths = months.error;
    }
    if (rate.ok) phases.push(phaseBody(1, toMonth, rate.value));
  } else {
    const froms = phaseFroms(form.phases);
    const last = form.phases.length - 1;
    if (form.phases.length === 0)
      errors['phases.0.rate'] = { key: 'commissionPlans.form.required' };
    if (form.phases.length > MAX_PHASES) {
      errors[`phases.${MAX_PHASES - 1}.to`] = {
        key: 'commissionPlans.form.maxPhases',
        params: { max: MAX_PHASES },
      };
    }
    for (const [i, phase] of form.phases.entries()) {
      const from = froms[i] ?? null;
      const to = phase.to.trim();
      let toMonth: number | null = null;
      if (to === '') {
        if (i !== last) errors[`phases.${i}.to`] = { key: 'commissionPlans.form.onlyLastOpen' };
      } else {
        const parsed = parseWhole(to, 1);
        if (!parsed.ok) errors[`phases.${i}.to`] = parsed.error;
        else if (from !== null && parsed.value < from) {
          errors[`phases.${i}.to`] = { key: 'commissionPlans.form.toBeforeFrom', params: { from } };
        } else toMonth = parsed.value;
      }
      const rate = parseRate(phase.rate, locale);
      if (!rate.ok) errors[`phases.${i}.rate`] = rate.error;
      if (from !== null && rate.ok) phases.push(phaseBody(from, toMonth, rate.value));
    }
  }

  const coolOff = parseWhole(form.coolOff, 0);
  if (!coolOff.ok) errors.coolOff = coolOff.error;

  const extras = options.extras ? extrasBody(form, locale, options.currency, errors) : {};

  if (Object.keys(errors).length > 0 || !coolOff.ok) return { body: null, errors };
  return {
    body: {
      name,
      mode: form.mode,
      appliesTo: form.appliesTo,
      coolOffDays: coolOff.value,
      afterEnd: form.afterEnd,
      phases,
      ...extras,
    },
    errors,
  };
}

type ParsedText = { ok: true; text: string } | { ok: false; error: FormError };

function parsedPercent(value: string, fromLocale: string, toLocale: string): ParsedText {
  const parsed = parseRate(value, fromLocale);
  if (!parsed.ok) return parsed;
  return { ok: true, text: bpToPercentInput(parsed.value, toLocale) };
}

function parsedWhole(value: string, min: number): ParsedText {
  const parsed = parseWhole(value, min);
  if (!parsed.ok) return parsed;
  return { ok: true, text: String(parsed.value) };
}

function parsedMoney(
  value: string,
  currency: string,
  fromLocale: string,
  toLocale: string,
): ParsedText {
  const parsed = parseAmount(value, currency, fromLocale);
  if (!parsed.ok) return parsed;
  return { ok: true, text: moneyInput(parsed.value, currency, toLocale) };
}

function parsedPayment(value: string): ParsedText {
  const trimmed = value.trim();
  if (WHOLE.test(trimmed) && Number(trimmed) >= 1 && Number(trimmed) <= BOUNTY_PAYMENTS) {
    return { ok: true, text: String(Number(trimmed)) };
  }
  return { ok: false, error: { key: 'commissionPlans.form.paymentNumber' } };
}

function applyParsed(
  errors: FormErrors,
  field: FormField,
  mark: boolean,
  parsed: ParsedText,
): string {
  if (parsed.ok) return parsed.text;
  if (mark) errors[field] = parsed.error;
  return '';
}

/**
 * A dirty form, field by field, in `toLocale` (TMMRSL-0054). Each amount, percent and integer —
 * and each phase and milestone cell — that parses in `fromLocale` is rewritten in `toLocale`.
 * One that doesn't is cleared. `errors` names the cells the current mode actually checks, with
 * the client error of the text that failed; a hidden cell is cleared so it can't be misread, but
 * not marked. Name, mode and the switches stay as typed. Relational checks (a "to" before its
 * "from", milestone order) are not parse failures.
 */
export function reformatForm(
  form: PlanForm,
  fromLocale: string,
  toLocale: string,
  options: ExtrasOptions = { extras: false, currency: '' },
): { form: PlanForm; errors: FormErrors } {
  const errors: FormErrors = {};
  const advanced = form.mode === 'ADVANCED';
  const showExtras = options.extras && advanced;
  const next: PlanForm = {
    ...form,
    phases: form.phases.map((phase) => ({ ...phase })),
    milestones: form.milestones.map((row) => ({ ...row })),
  };

  next.simpleRate = applyParsed(
    errors,
    'simpleRate',
    !advanced,
    parsedPercent(form.simpleRate, fromLocale, toLocale),
  );
  next.simpleMonths = applyParsed(
    errors,
    'simpleMonths',
    !advanced && !form.simpleLifetime,
    parsedWhole(form.simpleMonths, 1),
  );

  const last = form.phases.length - 1;
  next.phases = form.phases.map((phase, i) => {
    const to = phase.to.trim();
    let toText = '';
    if (to === '') {
      if (i !== last && advanced) {
        errors[`phases.${i}.to`] = { key: 'commissionPlans.form.onlyLastOpen' };
      }
    } else {
      toText = applyParsed(errors, `phases.${i}.to`, advanced, parsedWhole(phase.to, 1));
    }
    return {
      to: toText,
      rate: applyParsed(
        errors,
        `phases.${i}.rate`,
        advanced,
        parsedPercent(phase.rate, fromLocale, toLocale),
      ),
    };
  });

  next.coolOff = applyParsed(errors, 'coolOff', true, parsedWhole(form.coolOff, 0));

  if (options.extras) {
    const markBounty = showExtras && form.bountyOn;
    next.bounty = applyParsed(
      errors,
      'bounty',
      markBounty,
      parsedMoney(form.bounty, options.currency, fromLocale, toLocale),
    );
    next.bountyOnPayment = applyParsed(
      errors,
      'bountyOnPayment',
      markBounty,
      parsedPayment(form.bountyOnPayment),
    );
    next.milestones = form.milestones.map((row, i) => ({
      at: applyParsed(errors, `milestones.${i}.at`, showExtras, parsedWhole(row.at, 1)),
      amount: applyParsed(
        errors,
        `milestones.${i}.amount`,
        showExtras,
        parsedMoney(row.amount, options.currency, fromLocale, toLocale),
      ),
    }));
    next.referralCap = applyParsed(
      errors,
      'referralCap',
      showExtras && form.referralCapOn,
      parsedMoney(form.referralCap, options.currency, fromLocale, toLocale),
    );
    next.monthCap = applyParsed(
      errors,
      'monthCap',
      showExtras && form.monthCapOn,
      parsedMoney(form.monthCap, options.currency, fromLocale, toLocale),
    );
  }

  return { form: next, errors };
}

/** A field's current value, for telling whether the user changed it after the server named it. */
export function fieldValue(form: PlanForm, field: FormField): string {
  const phase = /^phases\.(\d+)\.(to|rate)$/.exec(field);
  if (phase) {
    const row = form.phases[Number(phase[1])];
    return row ? row[phase[2] as keyof PhaseForm] : '';
  }
  const milestone = /^milestones\.(\d+)\.(at|amount)$/.exec(field);
  if (milestone) {
    const row = form.milestones[Number(milestone[1])];
    return row ? row[milestone[2] as keyof MilestoneForm] : '';
  }
  if (field === 'simpleMonths') return form.simpleLifetime ? 'life' : form.simpleMonths;
  // A switched-off extra has no value of its own.
  if (field === 'bounty') return form.bountyOn ? form.bounty : 'off';
  if (field === 'bountyOnPayment') return form.bountyOn ? form.bountyOnPayment : 'off';
  if (field === 'referralCap') return form.referralCapOn ? form.referralCap : 'off';
  if (field === 'monthCap') return form.monthCapOn ? form.monthCap : 'off';
  return String(form[field as Exclude<FormField, PhaseField | MilestoneField>]);
}

/** The form's body-relevant values: a switched-off extra is off whatever its text. */
function bodyRelevant(form: PlanForm): PlanForm {
  return {
    ...form,
    bounty: form.bountyOn ? form.bounty : '',
    bountyOnPayment: form.bountyOn ? form.bountyOnPayment : '',
    referralCap: form.referralCapOn ? form.referralCap : '',
    monthCap: form.monthCapOn ? form.monthCap : '',
  };
}

/** Whether the form's body-relevant values differ from the plan's (TMMRSL-0053: extras). */
export function isDirty(form: PlanForm, plan: CommissionPlan, locale: string): boolean {
  return (
    JSON.stringify(bodyRelevant(form)) !== JSON.stringify(bodyRelevant(planToForm(plan, locale)))
  );
}

/**
 * The plan with a parsed write body's values over it (TMMRSL-0053) — re-seeding a dirty form in a
 * new locale: `planToForm(planWithBody(plan, formToBody(form, oldLocale).body), newLocale)`.
 */
export function planWithBody(plan: CommissionPlan, body: CommissionPlanWrite): CommissionPlan {
  return {
    ...plan,
    name: body.name ?? plan.name,
    mode: body.mode ?? plan.mode,
    appliesTo: body.appliesTo ?? plan.appliesTo,
    coolOffDays: body.coolOffDays ?? plan.coolOffDays,
    afterEnd: body.afterEnd ?? plan.afterEnd,
    phases: body.phases
      ? body.phases.map((phase) => ({
          fromMonth: phase.fromMonth,
          toMonth: phase.toMonth ?? null,
          rateBp: phase.rateBp,
        }))
      : plan.phases,
    extras: {
      bountyMinor: body.bountyMinor || null,
      bountyOnPayment: body.bountyOnPayment ?? plan.extras.bountyOnPayment,
      milestones: body.milestones ?? [],
      perReferralCapMinor: body.perReferralCapMinor || null,
      perAffiliateMonthCapMinor: body.perAffiliateMonthCapMinor || null,
    },
  };
}

const PHASE_KEY = /^phases(?:\[(\d+)\]|\.(\d+))\.(rateBp|toMonth|fromMonth)$/;
const MILESTONE_KEY = /^milestones(?:\[(\d+)\]|\.(\d+))\.(qualifiedReferrals|amountMinor)$/;
const PLAIN: Partial<Record<string, FormField>> = {
  name: 'name',
  mode: 'mode',
  appliesTo: 'appliesTo',
  afterEnd: 'afterEnd',
  coolOffDays: 'coolOff',
};
/** The extras' server keys (TMMAPI-0105) — shown in ADVANCED only. */
const EXTRAS: Partial<Record<string, FormField>> = {
  bountyMinor: 'bounty',
  bountyOnPayment: 'bountyOnPayment',
  milestones: 'milestones.0.at',
  perReferralCapMinor: 'referralCap',
  perAffiliateMonthCapMinor: 'monthCap',
};

/**
 * The form field a server key names, in the form's current mode (`phases[1].rateBp` →
 * `phases.1.rate`; in SIMPLE, phase 0 is the Simple fields), or `null` for a key the form doesn't
 * show. A phase's "from" is never typed — it follows the row above — so `phases[i].fromMonth` marks
 * row i−1's "to", and for row 0 its rate. `rateCardId` is no form field (the example's card picker).
 * The extras (`bountyMinor`, `milestones[i].amountMinor`, …) have fields in ADVANCED only.
 */
export function serverFieldFor(key: string, mode: CommissionPlanMode): FormField | null {
  if (Object.hasOwn(PLAIN, key)) return PLAIN[key] ?? null;
  if (Object.hasOwn(EXTRAS, key)) return mode === 'SIMPLE' ? null : (EXTRAS[key] ?? null);
  const milestone = MILESTONE_KEY.exec(key);
  if (milestone) {
    const index = Number(milestone[1] ?? milestone[2]);
    if (mode === 'SIMPLE' || index >= MAX_MILESTONES) return null;
    return milestone[3] === 'amountMinor' ? `milestones.${index}.amount` : `milestones.${index}.at`;
  }
  if (key === 'phases') return mode === 'SIMPLE' ? 'simpleRate' : 'phases.0.to';
  const match = PHASE_KEY.exec(key);
  if (!match) return null;
  const named = Number(match[1] ?? match[2]);
  const from = match[3] === 'fromMonth';
  const index = from && named > 0 ? named - 1 : named;
  const rate = match[3] === 'rateBp' || (from && named === 0);
  if (mode === 'SIMPLE') {
    if (index !== 0) return null;
    return rate ? 'simpleRate' : 'simpleMonths';
  }
  if (index >= MAX_PHASES) return null;
  return rate ? `phases.${index}.rate` : `phases.${index}.to`;
}

/** Advanced → Simple needs exactly one phase row, and no extras in use. */
export function canSwitchToSimple(form: PlanForm): boolean {
  return form.phases.length <= 1 && !extrasInUse(form);
}

/**
 * The Simple toggle's tooltip while it's blocked: the extras key when they're the only blocker,
 * else the one-phase key.
 */
export function simpleBlockedKey(form: PlanForm): string {
  return form.phases.length <= 1 && extrasInUse(form)
    ? 'commissionPlans.mode.simpleNeedsBasic'
    : 'commissionPlans.mode.simpleNeedsOne';
}

/** A new blank milestone row; nothing past the fifth. */
export function addMilestone(milestones: MilestoneForm[]): MilestoneForm[] {
  return milestones.length >= MAX_MILESTONES ? milestones : [...milestones, { at: '', amount: '' }];
}

export function removeMilestone(milestones: MilestoneForm[], index: number): MilestoneForm[] {
  return milestones.filter((_, i) => i !== index);
}

/**
 * Switch the editor's mode, carrying the values across. Simple → Advanced: the one phase becomes
 * row 0 ("to" = the months, or blank for life). Advanced → Simple (one row only): the row's rate is
 * the Simple rate, its "to" the months, or "For life" when blank.
 */
export function switchMode(form: PlanForm, mode: CommissionPlanMode): PlanForm {
  if (form.mode === mode) return form;
  if (mode === 'ADVANCED') {
    return {
      ...form,
      mode,
      phases: [{ to: form.simpleLifetime ? '' : form.simpleMonths, rate: form.simpleRate }],
    };
  }
  if (!canSwitchToSimple(form)) return form;
  const row = form.phases[0];
  if (!row) return { ...form, mode };
  const to = row.to.trim();
  return {
    ...form,
    mode,
    simpleRate: row.rate,
    simpleLifetime: to === '',
    simpleMonths: to === '' ? form.simpleMonths : row.to,
  };
}

/**
 * A new last row with a blank "to". When the previous last row ran onward, it gets a 12-month
 * stretch ("from" + 11), so the new row is reachable. Nothing past the sixth row.
 */
export function addPhase(phases: PhaseForm[]): PhaseForm[] {
  if (phases.length >= MAX_PHASES) return phases;
  const froms = phaseFroms(phases);
  const lastIndex = phases.length - 1;
  const last = phases[lastIndex];
  const lastFrom = froms[lastIndex] ?? null;
  const closed =
    last && last.to.trim() === '' && lastFrom !== null
      ? phases.map((phase, i) =>
          i === lastIndex ? { ...phase, to: String(lastFrom + 11) } : phase,
        )
      : phases;
  return [...closed, { to: '', rate: '' }];
}

export function removePhase(phases: PhaseForm[], index: number): PhaseForm[] {
  return phases.length <= 1 ? phases : phases.filter((_, i) => i !== index);
}
